import type { Entitlements, PaidPlan, PaywallSource, PlanQuota, PlanQuotas } from './schemas/plans'

/**
 * 결과 화면 업셀 (로드맵 매출 3위, 2026-10-29). 스위치는 settings.resultUpsell + features.resultUpsell 두 겹이다.
 * 이 파일은 판정만 한다 — 언제 자동 페이월을 띄울지, 업셀 줄에 무엇을 보일지. 기기 기록과 화면은 앱 result-upsell.ts
 */

const HOUR_MS = 60 * 60 * 1000

/**
 * 사용자가 누르지 않았는데 뜨는 페이월(자동 페이월)의 빈도 상한. 숫자는 여기서만 바꾼다 — 앱이 거르고 관리자 설명이 그대로 보인다.
 * 이름은 페이월 source 와 같다. maxShows: 이 기기에서 몇 번까지. cooldownHours: 같은 트리거를 다시 띄우기까지 최소 간격
 */
export const AUTO_PAYWALL_LIMITS = {
  /** 첫 결과를 보고 나갈 때 (Free). 예전 '뒤로 가기 페이월' 기록을 이어 써서 이미 본 사람에게는 다시 뜨지 않는다 */
  first_result: { maxShows: 1, cooldownHours: 0, remainingAtMost: null },
  /** 남은 생성이 remainingAtMost 이하일 때 결과를 나가면 (Free·Basic, 체험 중 제외). 잠기기 직전에 미리 알린다 */
  near_limit: { maxShows: 3, cooldownHours: 72, remainingAtMost: 1 },
} as const satisfies Partial<
  Record<PaywallSource, { maxShows: number; cooldownHours: number; remainingAtMost: number | null }>
>
/** 트리거가 무엇이든 자동 페이월끼리 이 시간 안에는 다시 띄우지 않는다 */
export const AUTO_PAYWALL_GAP_HOURS = 24

export type AutoPaywallTrigger = keyof typeof AUTO_PAYWALL_LIMITS

/** 기기에 남긴 자동 페이월 기록. 시각은 epoch ms */
export interface AutoPaywallHistory {
  shows: Partial<Record<AutoPaywallTrigger, { count: number; lastAt: number }>>
}

export type AutoPaywallDecision =
  | { trigger: AutoPaywallTrigger; show: true }
  | { trigger: AutoPaywallTrigger; show: false; reason: 'cooldown' | 'gap' }

type PlanState = Pick<Entitlements, 'plan' | 'periodType' | 'generations'>

/** 이번 기간에 남은 생성 수 (산 팩 포함). Free 는 평생 기준 */
export function generationsRemaining(ent: Pick<Entitlements, 'generations'>): number {
  return Math.max(0, ent.generations.limit - ent.generations.used) + ent.generations.packRemaining
}

/** 결과를 나가는 지금 해당하는 트리거 (앞이 먼저). Pro 는 올릴 플랜이 없고, 체험 중에는 이미 기능이 다 열려 있다 */
function leaveTriggers(ent: PlanState): AutoPaywallTrigger[] {
  if (ent.plan === 'pro' || ent.periodType === 'trial') return []
  const triggers: AutoPaywallTrigger[] = []
  if (ent.plan === 'free') triggers.push('first_result')
  if (generationsRemaining(ent) <= AUTO_PAYWALL_LIMITS.near_limit.remainingAtMost) {
    triggers.push('near_limit')
  }
  return triggers
}

/**
 * 결과를 나갈 때 자동 페이월을 띄울지. 해당 트리거가 없거나 횟수를 다 썼으면 null(기록할 것도 없음).
 * 간격 때문에 못 띄우면 show:false 와 이유 — 상한이 얼마나 막는지 이벤트로 본다
 */
export function pickAutoPaywall(
  ent: PlanState,
  history: AutoPaywallHistory,
  now: number,
): AutoPaywallDecision | null {
  const shownAt = Object.values(history.shows).map((s) => s.lastAt)
  const lastAny = shownAt.length > 0 ? Math.max(...shownAt) : null
  let held: AutoPaywallDecision | null = null
  for (const trigger of leaveTriggers(ent)) {
    const rule = AUTO_PAYWALL_LIMITS[trigger]
    const seen = history.shows[trigger]
    if (seen && seen.count >= rule.maxShows) continue
    if (seen && now - seen.lastAt < rule.cooldownHours * HOUR_MS) {
      held ??= { trigger, show: false, reason: 'cooldown' }
      continue
    }
    if (lastAny !== null && now - lastAny < AUTO_PAYWALL_GAP_HOURS * HOUR_MS) {
      return { trigger, show: false, reason: 'gap' }
    }
    return { trigger, show: true }
  }
  return held
}

/** 띄운 뒤의 기록 */
export function recordAutoPaywall(
  history: AutoPaywallHistory,
  trigger: AutoPaywallTrigger,
  now: number,
): AutoPaywallHistory {
  const count = (history.shows[trigger]?.count ?? 0) + 1
  return { shows: { ...history.shows, [trigger]: { count, lastAt: now } } }
}

/** 업셀 줄에 보일 수 있는 것 (앞이 먼저). value 는 다음 플랜의 숫자, 켜고 끄는 기능은 null */
export const UPSELL_ITEMS = [
  'photos',
  'competitors',
  'specSheet',
  'generations',
  'marketSelect',
  'includedQuotes',
  'regenerations',
] as const
export type UpsellItem = (typeof UPSELL_ITEMS)[number]

export interface UpsellOffer {
  nextPlan: PaidPlan
  items: { key: UpsellItem; value: number | null }[]
}

/** 다음 플랜에서 늘어나는 것인지와 그 값 */
function gain(
  key: UpsellItem,
  current: PlanQuota,
  next: PlanQuota,
  specSheetVisible: boolean,
): { key: UpsellItem; value: number | null } | null {
  switch (key) {
    case 'specSheet':
      return specSheetVisible && !current.specSheet && next.specSheet ? { key, value: null } : null
    case 'marketSelect':
      return !current.marketSelect && next.marketSelect ? { key, value: null } : null
    default:
      return next[key] > current[key] ? { key, value: next[key] } : null
  }
}

/**
 * 결과 화면 업셀 줄 — 지금 플랜에서 한 단계 올리면 늘어나는 것을 많아야 max 개 (한 줄에 들어가게 기본 2). 체험 중·Pro·늘어나는 것이 없으면 null.
 * 숫자는 관리자 planQuotas 를 따른다. 사양서는 그 버튼이 보일 때만 말한다 (없는 기능을 팔지 않는다)
 */
export function upsellOffer(
  ent: Pick<Entitlements, 'plan' | 'periodType'>,
  quotas: PlanQuotas,
  specSheetVisible: boolean,
  max = 2,
): UpsellOffer | null {
  if (ent.periodType === 'trial' || ent.plan === 'pro') return null
  const nextPlan: PaidPlan = ent.plan === 'free' ? 'basic' : 'pro'
  const items = UPSELL_ITEMS.flatMap((key) => {
    const item = gain(key, quotas[ent.plan], quotas[nextPlan], specSheetVisible)
    return item ? [item] : []
  }).slice(0, max)
  return items.length > 0 ? { nextPlan, items } : null
}
